import { type EstimatedEvent } from '@solinkcorp/motion-events-estimator';
import { GLOBAL_COLORS } from '@solinkcorp/solink-components';
import { type IEvent } from 'clients/PlatformApi';
import {
  checkDownloadEventExpiration,
  isAccessControl,
  isActiveNow,
  isAlarm,
  isAlarmAndOpen,
  isDownloadBookmark,
  isInFuture,
  isScheduledActivity,
} from 'utils/EventUtil';

export type EventColorType =
  | Required<IEvent>['type']
  | EstimatedEvent['type']
  | 'search'
  | 'open-alarm'
  | 'accessControl'
  | 'alarm'
  | 'downloadExpired'
  | 'scheduledActivityInactive';

export const COLOR_FOR_EVENT_TYPE: { [color in EventColorType]: string } = {
  transaction: GLOBAL_COLORS['transactionColor'],
  motion: GLOBAL_COLORS['motionColor'],
  bookmark: GLOBAL_COLORS['bookmarkColor'],
  search: GLOBAL_COLORS['search-color'],
  accessControl: GLOBAL_COLORS['blue-2'],
  alarm: GLOBAL_COLORS['color-dark-gray-light'],
  'open-alarm': GLOBAL_COLORS['alarm-color'],
  estimated: GLOBAL_COLORS['estimatedColor'],
  downloadExpired: GLOBAL_COLORS['downloadExpiredColor'],
  scheduledActivityInactive: GLOBAL_COLORS['scheduledActivityInvalidColor'],
  error: '#f00',
};

export const COLOR_TRANSACTION_DARK = GLOBAL_COLORS['color-green'];

/**
 * Returns a "color name" for an event.
 */
export function getEventColorName(
  event: IEvent | EstimatedEvent,
  searchMatch: boolean,
): EventColorType | undefined {
  if (searchMatch) {
    return 'search';
  } else if (isAccessControl(event as IEvent)) {
    return 'accessControl';
  } else if (isAlarmAndOpen(event as IEvent)) {
    return 'open-alarm';
  } else if (isAlarm(event)) {
    return 'alarm';
  } else if (
    isDownloadBookmark(event as IEvent) &&
    checkDownloadEventExpiration(event as IEvent).hasExpired
  ) {
    return 'downloadExpired';
  } else if (isScheduledActivity(event as IEvent)) {
    if (
      (event as IEvent).state === 'CLOSED' ||
      (!isActiveNow(event as IEvent) && !isInFuture(event as IEvent))
    ) {
      return 'scheduledActivityInactive';
    }
    return event.type;
  } else if (event.type) {
    return event.type;
  }

  return undefined;
}

/**
 * Returns the color to use when drawing an event.
 */
export function getColorForEvent(event: IEvent | EstimatedEvent, searchMatch: boolean): string {
  const colorName = getEventColorName(event, searchMatch);
  if (colorName) {
    return COLOR_FOR_EVENT_TYPE[colorName];
  }
  return '#888';
}
